import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
// Living Results in the Result canvas (collaboration v1, PR 6 §1): the
// eyebrow that says which version this is and who wrote it, Edit for people
// with edit rights, Edit with chat for tables, charts and decks, Duplicate for
// readers, and History with Restore.
import { useEffect, useState } from 'react';
import { CompanyRequestError } from '@/company/api';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { WorkspaceMarkdown } from './markdown';
import { workspaceApi } from './api';
import { livingApi, relativeTime } from './living-api';
import type { ResultHistory, ResultRevisionEntry, ResultSharingCheck } from './living-api';
import type { CompanyResult, ResultRevisionLabel, WorkspaceTurnContext } from './types';

const firstName = (
  name: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) => (name ?? '').split(' ')[0] || uiText('Someone');

/** "You", a colleague's first name, "Assistant", the outside agent ("Claude"), or the agent. */
export function authorLabel(
  entry: Pick<ResultRevisionEntry, 'authorKind' | 'author' | 'clientName'> & {
    agentName?: string | null;
  },
  userId: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  if (entry.authorKind === 'agent') return entry.agentName ?? uiText('An agent');
  if (entry.authorKind === 'assistant') return entry.clientName || uiText('Assistant');
  if (entry.author?.userId && entry.author.userId === userId) return uiText('You');
  return firstName(entry.author?.name, uiText);
}

/** "· Saved by Maria", "· Edited by You", or an outside agent's External MCP save: "· Saved from Claude". */
function authorship(
  label: ResultRevisionLabel,
  userId: string | null | undefined,
  uiText: WorkspaceText,
) {
  if (label.authorKind === 'assistant' && label.clientName)
    return '· ' + uiText('Saved from {value1}', { value1: label.clientName });
  if (!label.authorKind) return null;
  const who = authorLabel({ authorKind: label.authorKind, author: label.author }, userId, uiText);
  return uiText('· {value1} by {value2}', {
    value1: label.current > 1 ? uiText('Edited') : uiText('Saved'),
    value2: who,
  });
}

/** The eyebrow's living part: the version and who wrote it, or the agent. */
export function livingEyebrow(
  result: Pick<CompanyResult, 'revision' | 'maintainedBy'>,
  userId: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  if (result.maintainedBy)
    return uiText('Kept up to date by {value1}{value2}', {
      value1: result.maintainedBy.name ?? uiText('an agent'),
      value2: result.maintainedBy.refreshedAt
        ? ' · ' +
          uiText('refreshed {time}', {
            time: relativeTime(result.maintainedBy.refreshedAt, undefined, uiText.locale),
          })
        : '',
    });
  const label = result.revision;
  if (!label) return '';
  const who = authorship(label, userId, uiText);
  const edited = who ? ' ' + who : '';
  const when = label.editedAt
    ? uiText(', {value1}', { value1: relativeTime(label.editedAt, undefined, uiText.locale) })
    : '';
  return uiText('Version {value1}{value2}{value3}', {
    value1: label.current,
    value2: edited,
    value3: when,
  });
}

/** The Library row's line under a Result's title. */
export function libraryLabel(
  result: Pick<CompanyResult, 'revision' | 'maintainedBy'>,
  userId: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  if (result.maintainedBy) return livingEyebrow(result, userId, uiText);
  const label = result.revision;
  if (!label || label.current < 2) return '';
  return livingEyebrow(result, userId, uiText);
}

const canEditContent = (result: CompanyResult) => result.canEdit && !result.maintainedBy;

export function LivingResultActions({
  result,
  context,
  onEdit,
}: {
  result: CompanyResult;
  context: WorkspaceTurnContext;
  onEdit: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [busy, setBusy] = useState(false);
  const discuss = (text: string) => ws.send(text, context);
  if (result.maintainedBy)
    return (
      <>
        {result.maintainedBy.agentId && (
          <button
            className="ws-button"
            onClick={() => {
              ws.openCanvas({ kind: 'agent', id: result.maintainedBy!.agentId! });
            }}
          >
            {uiRich('{value1} Change the agent', {
              value1: <WorkspaceIcon name="play" size={15} />,
            })}
          </button>
        )}
        <button className="ws-button" onClick={() => discuss(`Let's discuss “${result.title}”.`)}>
          {uiRich('{value1} Continue in chat', { value1: <WorkspaceIcon name="chat" size={15} /> })}
        </button>
      </>
    );
  if (canEditContent(result))
    return (
      <>
        {result.kind === 'markdown' && (
          <button className="ws-button primary" onClick={onEdit}>
            {uiText('Edit')}
          </button>
        )}
        <button
          className="ws-button"
          onClick={() => discuss(`Help me edit “${result.title}”. Keep it as the same Result.`)}
        >
          {uiRich('{value1} Edit with chat', { value1: <WorkspaceIcon name="chat" size={15} /> })}
        </button>
      </>
    );
  return (
    <>
      <button
        className="ws-button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void livingApi
            .duplicateResult(result, `Copy of ${result.title}`.slice(0, 300))
            .then(async ({ result: copy }) => {
              await ws.refresh();
              ws.openCanvas({ kind: 'result', id: copy.id });
              ws.notify(uiText('Duplicated to your Library. Only you can see the copy.'));
            })
            .catch(() => ws.notify(uiText('Could not duplicate this Result. Try again.')))
            .finally(() => setBusy(false));
        }}
      >
        {uiRich('{value1} Duplicate to my Library', {
          value1: <WorkspaceIcon name="files" size={15} />,
        })}
      </button>
      <button className="ws-button" onClick={() => discuss(`Let's discuss “${result.title}”.`)}>
        {uiRich('{value1} Continue in chat', { value1: <WorkspaceIcon name="chat" size={15} /> })}
      </button>
    </>
  );
}

type Conflict = { name: string; markdown: string | null; revision: number };

async function latestConflict(
  result: CompanyResult,
  userId: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const latest = await livingApi.history(result.id);
  const [newest] = latest.revisions;
  const current = await workspaceApi.result(result.id);
  return {
    name: newest ? authorLabel(newest, userId, uiText) : uiText('Someone'),
    markdown:
      current.kind === 'markdown'
        ? ((current.payload as { markdown: string })?.markdown ?? '')
        : null,
    revision: latest.currentRevision,
  };
}

/** Markdown edit mode: the text, a live preview, an optional note and Save. */
export function ResultEditor({
  result,
  markdown,
  onClose,
}: {
  result: CompanyResult;
  markdown: string;
  onClose: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const base = result.revision?.current ?? result.currentRevision ?? 1;
  const [text, setText] = useState(markdown);
  const [note, setNote] = useState('');
  const [baseRevision, setBaseRevision] = useState(base);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [showTheirs, setShowTheirs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async (uiText: WorkspaceText = workspaceTextFor('en')) => {
    setSaving(true);
    setError('');
    try {
      const saved = await livingApi.editResult(result.id, {
        baseRevision,
        payload: { markdown: text },
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      ws.notify(uiText('Saved version {value1}.', { value1: saved.revision }));
      await ws.refresh();
      onClose();
    } catch (failure) {
      if (failure instanceof CompanyRequestError && failure.code === 'result_conflict') {
        const newer = await latestConflict(result, ws.user?.id, uiText).catch(() => null);
        if (newer) {
          setConflict(newer);
          setBaseRevision(newer.revision);
        } else setError(uiText('A newer version was saved. Reload the Result before saving.'));
      } else setError(uiText('Could not save this version. Try again.'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="ws-result-editor">
      {conflict && (
        <div className="ws-result-conflict" role="alert">
          <span>
            {uiRich('{value1} saved a newer version. Review it before saving.', {
              value1: conflict.name,
            })}
          </span>
          {conflict.markdown !== null && (
            <button className="ws-text-button" onClick={() => setShowTheirs((value) => !value)}>
              {showTheirs ? uiText('Hide their version') : uiText('Show their version')}
            </button>
          )}
          {conflict.markdown !== null && (
            <button className="ws-text-button" onClick={() => setText(conflict.markdown ?? '')}>
              {uiText('Start from their version')}
            </button>
          )}
        </div>
      )}
      {conflict && showTheirs && conflict.markdown !== null && (
        <div className="ws-result-theirs">
          <WorkspaceMarkdown text={conflict.markdown} />
        </div>
      )}
      <div className="ws-result-edit-panes">
        <label className="ws-result-edit-text">
          <span>{uiText('Markdown')}</span>
          <textarea
            aria-label={uiText('Result text')}
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={16}
          />
        </label>
        <div className="ws-result-edit-preview" aria-label={uiText('Preview')}>
          <span>{uiText('Preview')}</span>
          <WorkspaceMarkdown text={text} />
        </div>
      </div>
      <div className="ws-actions ws-result-edit-actions">
        <input
          type="text"
          className="ws-result-note"
          aria-label={uiText('What changed')}
          placeholder={uiText('What changed? (optional)')}
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
        />
        <button className="ws-button" onClick={onClose} disabled={saving}>
          {uiText('Cancel')}
        </button>
        <button
          className="ws-button primary"
          onClick={() => void save(uiText)}
          disabled={saving || !text.trim()}
        >
          {uiRich('Save version {value1}', { value1: baseRevision + 1 })}
        </button>
      </div>
      {error && <p className="ws-error">{error}</p>}
    </div>
  );
}

function entryNote(entry: ResultRevisionEntry, uiText: WorkspaceText = workspaceTextFor('en')) {
  if (entry.note) return entry.note;
  if (entry.authorKind === 'agent') return uiText('Refreshed by its agent');
  if (entry.authorKind === 'assistant' && entry.clientName)
    return uiText('Saved from {value1}', { value1: entry.clientName });
  if (entry.authorKind === 'assistant') return uiText('Saved from a chat');
  return entry.revision === 1 ? uiText('Saved') : uiText('Edited');
}

function HistoryRow({
  entry,
  current,
  canRestore,
  onRestore,
}: {
  entry: ResultRevisionEntry;
  current: boolean;
  canRestore: boolean;
  onRestore: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const author = authorLabel({ ...entry, agentName: entry.origin.agentName }, ws.user?.id, uiText);
  return (
    <div className="ws-history-row">
      <span className="ws-history-version">v{entry.revision}</span>
      <span className="ws-history-main">
        <b>{entryNote(entry, uiText)}</b>
        <small>
          <span className={`ws-author-badge ws-author-${entry.authorKind}`}>{author}</span>
          {' · '}
          {relativeTime(entry.createdAt, undefined, uiText.locale)}
          {entry.origin.threadId && (
            <>
              {' · '}
              <button
                className="ws-text-button"
                onClick={() => ws.openThread(entry.origin.threadId!)}
              >
                {uiText('Open chat')}
              </button>
            </>
          )}
        </small>
      </span>
      {current ? (
        <span className="ws-pill ws-pill-ok">{uiText('Current')}</span>
      ) : canRestore ? (
        <button className="ws-button" onClick={onRestore}>
          {uiText('Restore')}
        </button>
      ) : null}
    </div>
  );
}

/** History: every revision with its author, note and origin, and Restore. */
export function ResultHistoryList({ result }: { result: CompanyResult }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [history, setHistory] = useState<ResultHistory | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    livingApi
      .history(result.id)
      .then((value) => {
        if (active) setHistory(value);
      })
      .catch(() => {
        if (active) setError(uiText('Could not load the history.'));
      });
    return () => {
      active = false;
    };
  }, [result.id, result.revision?.current]);
  const restore = async (revision: number) => {
    if (!history) return;
    try {
      const restored = await livingApi.restore(result.id, revision, history.currentRevision);
      ws.notify(
        uiText('Restored version {value1} as version {value2}.', {
          value1: revision,
          value2: restored.revision,
        }),
      );
      await ws.refresh();
    } catch (failure) {
      ws.notify(
        failure instanceof CompanyRequestError && failure.code === 'result_conflict'
          ? uiText('Someone saved a newer version. Review it before restoring.')
          : uiText('Could not restore that version.'),
      );
    }
  };
  if (error) return <p className="ws-muted">{error}</p>;
  if (!history) return <p className="ws-muted">{uiText('Loading history…')}</p>;
  return (
    <div className="ws-history">
      {history.revisions.map((entry) => (
        <HistoryRow
          key={entry.revision}
          entry={entry}
          current={entry.revision === history.currentRevision}
          canRestore={history.canEdit}
          onRestore={() => void restore(entry.revision)}
        />
      ))}
      <p className="ws-page-note">
        {uiText('Restoring keeps what is there now as a version too, so nothing is lost.')}
      </p>
    </div>
  );
}

type PrivateConnection = ResultSharingCheck['privateConnections'][number];

function connectionName(
  connection: PrivateConnection,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const name = connection.name ?? connection.toolName ?? uiText('private account');
  return connection.accountLabel
    ? uiText('{value1} ({value2})', { value1: name, value2: connection.accountLabel })
    : name;
}

function colleagueConnection(
  connection: PrivateConnection,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const name = connection.name ?? connection.toolName ?? uiText('private account');
  return uiText('{value1}’s {value2}', {
    value1: connection.personName ?? uiText('a colleague'),
    value2: name,
  });
}

/**
 * Asked before a Result is shared, because sharing it is instant: the private
 * connections its chats used, and a personal agent that keeps it up to date.
 * It only warns — the share still goes through.
 */
export function ResultShareWarning({ id }: { id: string }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const [check, setCheck] = useState<ResultSharingCheck | null>(null);
  useEffect(() => {
    let active = true;
    livingApi
      .sharingCheck(id)
      .then((value) => {
        if (active) setCheck(value);
      })
      .catch(() => {
        if (active) setCheck(null);
      });
    return () => {
      active = false;
    };
  }, [id]);
  if (!check?.warn) return null;
  const own = check.privateConnections.filter((connection) => connection.own);
  const theirs = check.privateConnections.filter((connection) => !connection.own);
  const agent = check.maintainedByPersonalAgent;
  return (
    <div className="ws-result-conflict" role="note">
      {own.length > 0 && (
        <span>
          {uiRich('This Result was made with your own {value1}.', {
            value1: own.map((connection) => connectionName(connection, uiText)).join(', '),
          })}
        </span>
      )}
      {theirs.length > 0 && (
        <span>
          {own.length > 0 ? uiText('It also used') : uiText('This Result was made with')}{' '}
          {theirs.map((connection) => colleagueConnection(connection, uiText)).join(', ')}.
        </span>
      )}
      {check.privateConnections.length > 0 && (
        <span>{uiText('Colleagues can read what came from them, but can never use them.')}</span>
      )}
      {agent &&
        (agent.own ? (
          <span>
            {uiRich(
              'Your personal agent {value1} keeps it up to date with your own access, so every refresh can carry your private data.',
              { value1: agent.name },
            )}
          </span>
        ) : (
          <span>
            {uiText(
              'A colleague’s personal agent keeps it up to date with their own access, so every refresh can carry their private data.',
            )}
          </span>
        ))}
    </div>
  );
}
